import { Box, BoxProps } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useGovernedMotion } from "components/ds/frameGovernor";

/**
 * LunaAmbientBackground — the subtle drifting blur-blob backdrop the Luna setup
 * flow uses behind its icon while scanning. Ported into employee-portal so the
 * Luna analysis loading/scanning phase can reuse the same treatment (PLO-4818).
 *
 * Soft radial blobs in the two Luna brand colours (luna.0 indigo, luna.100
 * coral) drift slowly on independent looping paths. Absolutely positioned to
 * fill its (position: relative) parent; put the Luna icon + copy above it with
 * position="relative" zIndex={1}. Respects prefers-reduced-motion.
 */

// Independent looping drift paths — each blob wanders a different route so the
// field never looks like it's pulsing in unison.
const journey1 = keyframes`
  0%   { transform: translate3d(0%, 0%, 0) scale(1); }
  25%  { transform: translate3d(55%, 18%, 0) scale(1.15); }
  50%  { transform: translate3d(80%, 65%, 0) scale(0.9); }
  75%  { transform: translate3d(25%, 80%, 0) scale(1.1); }
  100% { transform: translate3d(0%, 0%, 0) scale(1); }
`;
const journey2 = keyframes`
  0%   { transform: translate3d(0%, 0%, 0) scale(1); }
  25%  { transform: translate3d(-50%, 55%, 0) scale(1.12); }
  50%  { transform: translate3d(-95%, 25%, 0) scale(0.92); }
  75%  { transform: translate3d(-45%, -12%, 0) scale(1.06); }
  100% { transform: translate3d(0%, 0%, 0) scale(1); }
`;
const journey3 = keyframes`
  0%   { transform: translate3d(0%, 0%, 0) scale(1); }
  33%  { transform: translate3d(30%, -55%, 0) scale(1.18); }
  66%  { transform: translate3d(-35%, -85%, 0) scale(0.95); }
  100% { transform: translate3d(0%, 0%, 0) scale(1); }
`;

// low-alpha washes of the two Luna brand tokens
const washBlue = (a: number) =>
  `radial-gradient(closest-side, color-mix(in srgb, var(--chakra-colors-luna-0) ${a}%, transparent), transparent)`;
const washCoral = (a: number) =>
  `radial-gradient(closest-side, color-mix(in srgb, var(--chakra-colors-luna-100) ${a}%, transparent), transparent)`;

const Blob = ({
  anim,
  duration,
  animate,
  sx,
  ...rest
}: BoxProps & { anim: ReturnType<typeof keyframes>; duration: string; animate: boolean }) => (
  <Box
    position="absolute"
    borderRadius="full"
    pointerEvents="none"
    willChange="transform"
    animation={animate ? `${anim} ${duration} ease-in-out infinite` : undefined}
    {...rest}
    // Merge the caller's sx but keep the reduced-motion guard last so it always
    // wins — every usage passes its own sx, which would otherwise clobber it.
    sx={{ ...sx, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
  />
);

const LunaAmbientBackground = ({ animate = true }: { animate?: boolean }) => {
  /* the drift loops free-run for the whole scanning phase — govern them (the
     journeys are 38–54s, so the 12fps base tier is imperceptible) */
  const rootRef = useGovernedMotion<HTMLDivElement>(false, animate);
  return (
    <Box ref={rootRef} position="absolute" inset="0" overflow="hidden" pointerEvents="none" aria-hidden>
      <Blob anim={journey1} duration="46s" animate={animate} top="-180px" left="-10%" w="55%" sx={{ aspectRatio: "1" }} bgImage={washBlue(17)} />
      <Blob anim={journey2} duration="38s" animate={animate} top="-140px" right="-14%" w="58%" sx={{ aspectRatio: "1" }} bgImage={washCoral(16)} />
      <Blob anim={journey3} duration="54s" animate={animate} top="180px" right="-8%" w="52%" sx={{ aspectRatio: "1" }} bgImage={washBlue(10)} />
    </Box>
  );
};

export default LunaAmbientBackground;
